import { addHours, addMinutes, differenceInMinutes, parseISO } from "date-fns";
import { max, min } from "lodash";
import { HosDriverDto } from "../../common/data/service-proxies";

export interface HosTimers {
  break: number;
  shiftDrive: number;
  shiftWork: number;
  cycleWork: number;
  cycleDrive: number;
  regularShiftWork: number;
  regularShiftDrive: number;
  breakViolationFrom?: Date;
  shiftDriveViolationFrom?: Date;
  shiftWorkViolationFrom?: Date;
  cycleWorkViolationFrom?: Date;
}

export const getHosTimers = (
  input?: Partial<HosDriverDto> | null,
  time: Date = new Date()
): HosTimers => {
  const retVal = {
    break: 0,
    shiftDrive: 0,
    shiftWork: 0,
    cycleDrive: 0,
    cycleWork: 0,
    regularShiftWork: 0,
    regularShiftDrive: 0,
  };

  if (!input) {
    return retVal;
  }

  calculateCycleTimes(input, retVal, time);
  calculateShiftWorkTime(input, retVal, time);
  calculateShiftDriveTime(input, retVal, time);
  calculateDrivingRunTime(input, retVal, time);

  return {
    breakViolationFrom:
      retVal.break < 0 && input.driverDutyStatus === "D"
        ? addMinutes(time, retVal.break)
        : undefined,
    shiftDriveViolationFrom:
      retVal.shiftDrive < 0 && input.driverDutyStatus === "D"
        ? addMinutes(time, retVal.shiftDrive)
        : undefined,
    shiftWorkViolationFrom:
      retVal.shiftWork < 0 && input.driverDutyStatus === "D"
        ? addMinutes(time, retVal.shiftWork)
        : undefined,
    cycleWorkViolationFrom:
      retVal.cycleWork < 0 && input.driverDutyStatus === "D"
        ? addMinutes(time, retVal.cycleWork)
        : undefined,
    break: max([0, retVal.break])!,
    shiftDrive: max([0, retVal.shiftDrive])!,
    shiftWork: max([0, retVal.shiftWork])!,
    cycleDrive: max([0, retVal.cycleDrive])!,
    cycleWork: max([0, retVal.cycleWork])!,
    regularShiftDrive: max([0, retVal.regularShiftDrive])!,
    regularShiftWork: max([0, retVal.regularShiftWork])!,
  };
};

const calculateCycleTimes = (
  input: Partial<HosDriverDto>,
  retVal: HosTimers,
  time: Date
) => {
  if (!input.driverDutyStatusStartTime || !input.maxCycle) {
    return;
  }

  if (input.cycleResetTime && parseISO(input.cycleResetTime) < time) {
    retVal.cycleWork = input.maxCycle;
  } else if (input.mustEndCycleWorkBy) {
    retVal.cycleWork = differenceInMinutes(
      parseISO(input.mustEndCycleWorkBy),
      time
    );
  } else {
    retVal.cycleWork = input.maxCycle - (input.cycleWorkTime ?? 0);
    if (
      input.driverDutyStatus === "D" ||
      input.driverDutyStatus === "ON" ||
      input.driverDutyStatus === "YM"
    ) {
      retVal.cycleWork -= differenceInMinutes(
        time,
        parseISO(input.driverDutyStatusStartTime)
      );
    }
  }

  if (input.cycleResetTime && parseISO(input.cycleResetTime) < time) {
    retVal.cycleDrive = input.maxCycle;
  } else if (input.mustEndCycleDrivingBy) {
    retVal.cycleDrive = differenceInMinutes(
      parseISO(input.mustEndCycleDrivingBy),
      time
    );
  } else {
    retVal.cycleDrive = input.maxCycle - (input.cycleDriveTime ?? 0);
    if (input.driverDutyStatus === "D") {
      retVal.cycleDrive -= differenceInMinutes(
        time,
        parseISO(input.driverDutyStatusStartTime)
      );
    }
  }

  retVal.cycleDrive = min([retVal.cycleDrive, retVal.cycleWork])!;
};

const calculateShiftWorkTime = (
  input: Partial<HosDriverDto>,
  retVal: HosTimers,
  time: Date
) => {
  if (!input.driverDutyStatusStartTime || !input.maxShift) {
    return;
  }

  // Has shift reset time passed?
  if (input.shiftResetTime && parseISO(input.shiftResetTime) < time) {
    retVal.shiftWork = input.maxShift;
  } else {
    // Do we have the shift end time?
    if (input.mustEndShiftBy) {
      retVal.shiftWork = differenceInMinutes(
        parseISO(input.mustEndShiftBy),
        time
      );
    }
    // Else, calculate the remaining work time
    else {
      retVal.shiftWork = input.maxShift - (input.shiftWorkTime ?? 0);

      // If the currently active duty status is one of the on duty statuses, decrease the remaining work time by the duration of the on duty
      if (
        input.driverDutyStatus === "D" ||
        input.driverDutyStatus === "ON" ||
        input.driverDutyStatus === "YM"
      ) {
        retVal.shiftWork -= differenceInMinutes(
          time,
          parseISO(input.driverDutyStatusStartTime)
        );
      }
    }

    // see if there were any partial split breaks
    let splitSleeperTime;
    let splitOffTime;
    if (input.eligibleForFirstSplitSleeperBreakAt) {
      splitSleeperTime = parseISO(input.eligibleForFirstSplitSleeperBreakAt);
    }

    if (input.eligibleForFirstSplitOffDutyBreakAt) {
      splitOffTime = parseISO(input.eligibleForFirstSplitOffDutyBreakAt);
    }

    if (splitSleeperTime && splitSleeperTime <= time) {
      const breakDuration = differenceInMinutes(
        time,
        parseISO(input.driverDutyStatusStartTime)
      );

      // increase the remaining work time if there was a SB status which lasted for more than 8 hours
      if (breakDuration > 7 * 60) {
        retVal.shiftWork += breakDuration;
      }
    } else if (splitOffTime && splitOffTime <= time) {
      const breakDuration = differenceInMinutes(
        time,
        parseISO(input.driverDutyStatusStartTime)
      );

      // increase the remaining work time if there was an off duty status which lasted for more than 3 hours
      if (breakDuration > 2 * 60) {
        retVal.shiftWork += breakDuration;
      }
    }

    // Has the driver completed the split break but not had any duty status afterwards?
    if (
      input.completesSplitSleeperBreakAt &&
      parseISO(input.completesSplitSleeperBreakAt) < time
    ) {
      if (input.firstSplitBreakStartTime && input.shiftStartTime) {
        retVal.shiftWork += differenceInMinutes(
          parseISO(input.firstSplitBreakStartTime),
          parseISO(input.shiftStartTime)
        );

        console.log(
          "Split sleeper break has completed - new remaining shift work time",
          retVal.shiftWork
        );
      } else {
        console.error(
          "The driver has completed the split sleeper break, but some of the required information is missing",
          "firstSplitBreakStartTime:",
          input.firstSplitBreakStartTime,
          "shiftStartTime:",
          input.shiftStartTime
        );
        retVal.shiftWork = input.maxShift - (input.shiftWorkTime ?? 0);
      }
    }
  }

  retVal.shiftWork = min([retVal.cycleWork, retVal.shiftWork])!;

  if (input.shiftResetTime && parseISO(input.shiftResetTime) < time) {
    retVal.regularShiftWork = input.maxShift;
  } else if (input.mustEndRegularShiftBy) {
    retVal.regularShiftWork = differenceInMinutes(
      parseISO(input.mustEndRegularShiftBy),
      time
    );
  } else {
    retVal.regularShiftWork =
      input.maxShift - (input.regularShiftWorkTime ?? 0);
    if (
      input.driverDutyStatus === "D" ||
      input.driverDutyStatus === "ON" ||
      input.driverDutyStatus === "YM"
    ) {
      retVal.regularShiftWork -= differenceInMinutes(
        time,
        parseISO(input.driverDutyStatusStartTime)
      );
    }
  }

  retVal.regularShiftWork = min([retVal.shiftWork, retVal.regularShiftWork])!;
};

const calculateShiftDriveTime = (
  input: Partial<HosDriverDto>,
  retVal: HosTimers,
  time: Date
) => {
  if (!input.driverDutyStatusStartTime || !input.maxDrive) {
    return retVal;
  }

  // Has shift reset time passed?
  if (input.shiftResetTime && parseISO(input.shiftResetTime) < time) {
    retVal.shiftDrive = input.maxDrive;
    retVal.regularShiftDrive = input.maxDrive;

    // Do we have the shift driving end time?
  } else {
    if (input.mustEndShiftDrivingBy) {
      retVal.shiftDrive = differenceInMinutes(
        parseISO(input.mustEndShiftDrivingBy),
        time
      );
      retVal.regularShiftDrive = retVal.shiftDrive;
    } else {
      retVal.shiftDrive = input.maxDrive - (input.shiftDriveTime ?? 0);
      if (input.driverDutyStatus === "D") {
        retVal.shiftDrive -= differenceInMinutes(
          time,
          parseISO(input.driverDutyStatusStartTime)
        );
      }

      retVal.regularShiftDrive = retVal.shiftDrive;
    }

    // Has the driver completed the split break but not had any duty status afterwards?
    if (
      input.completesSplitSleeperBreakAt &&
      parseISO(input.completesSplitSleeperBreakAt) < time
    ) {
      if (input.shiftDriveTimeAtFirstSplitBreak != null) {
        retVal.shiftDrive += input.shiftDriveTimeAtFirstSplitBreak;
      } else {
        console.error(
          "The driver has completed the split sleeper break, but some of the required information is missing",
          "shiftDriveTimeAtFirstSplitBreak:",
          input.shiftDriveTimeAtFirstSplitBreak
        );
        retVal.shiftDrive = input.maxDrive - (input.shiftDriveTime ?? 0);
      }
      // Else, calculate the drive time
    }
  }

  retVal.shiftDrive = min([retVal.shiftDrive, retVal.shiftWork])!;

  retVal.regularShiftDrive = min([
    retVal.regularShiftDrive,
    retVal.regularShiftWork,
  ])!;
};

const calculateDrivingRunTime = (
  input: Partial<HosDriverDto>,
  retVal: HosTimers,
  time: Date
) => {
  if (!input.driverDutyStatusStartTime) {
    return retVal;
  }

  if (input.shiftResetTime && parseISO(input.shiftResetTime) < time) {
    retVal.break = 8 * 60;
  } else if (input.mustHaveBreakBy) {
    retVal.break = differenceInMinutes(parseISO(input.mustHaveBreakBy), time);
  } else {
    if (input.driverDutyStatus === "D") {
      const mustHaveBreakBy = addHours(
        parseISO(input.driverDutyStatusStartTime),
        8
      );
      retVal.break = differenceInMinutes(mustHaveBreakBy, time);
    }
  }

  retVal.break = min([retVal.break, retVal.shiftDrive])!;
};
